import React, { useEffect, useMemo, useState } from 'react';
import { Box, Avatar, Paper, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Person as PersonIcon, SmartToy as BotIcon } from '@mui/icons-material';

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
}));

const Bubble = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 12,
  background: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: '100%',
}));

/**
 * ChatTypePairs
 * Animates through a list of {request, response} pairs, typing each message sequentially.
 */
const ChatTypePairs = ({
  pairs = [],
  typingSpeed = 35, // ms per char
  pauseBetweenUserAndAssistant = 600, // ms pause between user finishing and assistant starting
  pauseBetweenPairs = 1600, // ms pause after assistant finishes before moving to next pair
  showCursor = true,
  cursorCharacter = '|',
}) => {
  const safePairs = useMemo(() => pairs.filter(p => p && p.request && p.response), [pairs]);

  const [pairIndex, setPairIndex] = useState(0);
  const [phase, setPhase] = useState('typingUser'); // typingUser | waiting | typingAssistant | done
  const [userChars, setUserChars] = useState(0);
  const [assistantChars, setAssistantChars] = useState(0);
  const [cursorOn, setCursorOn] = useState(true);

  const current = safePairs.length ? safePairs[pairIndex % safePairs.length] : null;

  // Cursor blink
  useEffect(() => {
    if (!showCursor) return;
    const t = setInterval(() => setCursorOn(v => !v), 500);
    return () => clearInterval(t);
  }, [showCursor]);

  // Typing state machine
  useEffect(() => {
    if (!current) return;

    if (phase === 'typingUser') {
      if (userChars < current.request.length) {
        const t = setTimeout(() => setUserChars(c => c + 1), typingSpeed);
        return () => clearTimeout(t);
      }
      // Finished user, small pause then assistant
      const t = setTimeout(() => setPhase('typingAssistant'), pauseBetweenUserAndAssistant);
      return () => clearTimeout(t);
    }

    if (phase === 'typingAssistant') {
      if (assistantChars < current.response.length) {
        const t = setTimeout(() => setAssistantChars(c => c + 1), typingSpeed);
        return () => clearTimeout(t);
      }
      // Finished assistant, pause then next pair
      const t = setTimeout(() => {
        setPairIndex(i => (i + 1) % safePairs.length);
        setPhase('typingUser');
        setUserChars(0);
        setAssistantChars(0);
      }, pauseBetweenPairs);
      return () => clearTimeout(t);
    }
  }, [phase, userChars, assistantChars, current, typingSpeed, pauseBetweenUserAndAssistant, pauseBetweenPairs, safePairs.length]);

  if (!current) return null;

  const userDisplay = current.request.slice(0, userChars);
  const assistantDisplay = current.response.slice(0, assistantChars);

  const showAssistantRow = phase !== 'typingUser' || assistantChars > 0;
  const userCursorVisible = showCursor && phase === 'typingUser' && cursorOn;
  const assistantCursorVisible = showCursor && phase === 'typingAssistant' && cursorOn;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {/* User row */}
      <Row>
        <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28 }}>
          <PersonIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Bubble elevation={0}>
          <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}>
            {userDisplay}
            {userCursorVisible && <span style={{ opacity: 0.6, marginLeft: 1 }}>{cursorCharacter}</span>}
          </Typography>
        </Bubble>
      </Row>

      {/* Assistant row */}
      {showAssistantRow && (
        <Row>
          <Avatar sx={{ bgcolor: 'grey.300', color: 'grey.900', width: 28, height: 28 }}>
            <BotIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Bubble elevation={0}>
            {phase === 'typingAssistant' && assistantChars === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="body2" color="text.secondary">Completing...</Typography>
              </Box>
            ) : (
              <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}>
                {assistantDisplay}
                {assistantCursorVisible && <span style={{ opacity: 0.6, marginLeft: 1 }}>{cursorCharacter}</span>}
              </Typography>
            )}
          </Bubble>
        </Row>
      )}
    </Box>
  );
};

export default ChatTypePairs;
